import { useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { businessUnitsService } from '@/services/business-units.service';
import { selectedAccountIdAtom } from '@/hooks/use-account';
import { queryClient } from '@/lib/query-client';
import type {
  BusinessUnit,
  CreateBusinessUnitDto,
  UpdateBusinessUnitDto
} from '@/types';

const businessUnitsQueryAtom = atomWithQuery((get) => {
  const id = get(selectedAccountIdAtom);
  return {
    queryKey: ['business-units', id],
    enabled: id != null,
    queryFn: () => businessUnitsService.list(id as number)
  };
});

export const useBusinessUnits = () => {
  const [result] = useAtom(businessUnitsQueryAtom);
  return result;
};

/**
 * The brands of an EXPLICIT account, for a view that is not the operator's own.
 *
 * `useBusinessUnits` reads `selectedAccountIdAtom`, which is the account the Control Panel
 * is currently administering. Internal Analytics inspects a different account per panel and
 * several at once in the consolidated list, so it cannot use that atom without every panel
 * showing the same account's brands.
 *
 * Shares the `['business-units', accountId]` key with the atom above, so a brand renamed on
 * the Business Units tab invalidates this too.
 */
export const useAccountBusinessUnits = (accountId: number | null | undefined) => {
  const [data, setData] = useState<BusinessUnit[] | undefined>(undefined);

  useEffect(() => {
    if (accountId == null) return;
    let cancelled = false;
    queryClient
      .fetchQuery({
        queryKey: ['business-units', accountId],
        queryFn: () => businessUnitsService.list(accountId),
        staleTime: 5 * 60 * 1000
      })
      .then((rows) => {
        if (!cancelled) setData(rows);
      })
      .catch(() => {
        // A brand list that will not load must not take the metrics down with it: the
        // picker simply does not render and the page reads account-wide, which is what
        // it did before there was a picker at all.
        if (!cancelled) setData(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  return data;
};

export const useBusinessUnitMutations = () => {
  const id = useAtomValue(selectedAccountIdAtom);

  const requireId = (): number => {
    if (id == null) throw new Error('No account selected');
    return id;
  };
  const refresh = (accountId: number) =>
    queryClient.invalidateQueries({ queryKey: ['business-units', accountId] });

  return {
    createBusinessUnit: async (payload: CreateBusinessUnitDto) => {
      const accountId = requireId();
      const res = await businessUnitsService.create(accountId, payload);
      await refresh(accountId);
      return res;
    },
    updateBusinessUnit: async (buId: number, payload: UpdateBusinessUnitDto) => {
      const accountId = requireId();
      const res = await businessUnitsService.update(accountId, buId, payload);
      await refresh(accountId);
      return res;
    }
  };
};
